import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useFinePointer, useReducedMotion } from "@/hooks/hooks";

const DIMS = {
    default: { size: 34, bg: "rgba(0,128,255,0)", border: "rgba(0,128,255,0.4)" },
    interactive: { size: 54, bg: "rgba(0,128,255,0.08)", border: "rgba(0,128,255,0.85)" },
    button: { size: 60, bg: "rgba(0,128,255,0.12)", border: "rgba(0,128,255,0.9)" },
    view: { size: 76, bg: "rgba(0,128,255,0.92)", border: "rgba(0,128,255,0)", label: "VIEW" },
    drag: { size: 76, bg: "rgba(0,128,255,0.92)", border: "rgba(0,128,255,0)", label: "DRAG" },
};

export default function CustomCursor() {
    const fine = useFinePointer();
    const reduced = useReducedMotion();
    const dotRef = useRef(null);
    const ringRef = useRef(null);
    const [state, setState] = useState("default");

    useEffect(() => {
        if (!fine || reduced) return;
        document.body.classList.add("has-cursor");
        const pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        const ring = { ...pos };
        let raf;

        const onMove = (e) => {
            pos.x = e.clientX;
            pos.y = e.clientY;
            if (dotRef.current)
                dotRef.current.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
        };
        const onOver = (e) => {
            const tagged = e.target.closest?.("[data-cursor]");
            if (tagged) setState(tagged.dataset.cursor);
            else if (e.target.closest?.("a, button, input, textarea, select, [role='button'], summary, label"))
                setState("interactive");
            else setState("default");
        };
        const loop = () => {
            raf = requestAnimationFrame(loop);
            ring.x += (pos.x - ring.x) * 0.16;
            ring.y += (pos.y - ring.y) * 0.16;
            if (ringRef.current)
                ringRef.current.style.transform = `translate3d(${ring.x}px, ${ring.y}px, 0)`;
        };
        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("mouseover", onOver, { passive: true });
        raf = requestAnimationFrame(loop);
        return () => {
            document.body.classList.remove("has-cursor");
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseover", onOver);
            cancelAnimationFrame(raf);
        };
    }, [fine, reduced]);

    if (!fine || reduced) return null;
    const dim = DIMS[state] || DIMS.default;

    return (
        <>
            <div ref={dotRef} className="pointer-events-none fixed left-0 top-0 z-[999]" aria-hidden>
                <div className="h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-electric shadow-glow" />
            </div>
            <div ref={ringRef} className="pointer-events-none fixed left-0 top-0 z-[998]" aria-hidden>
                <motion.div
                    className="flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border font-mono text-[10px] tracking-widest text-white"
                    animate={{
                        width: dim.size,
                        height: dim.size,
                        backgroundColor: dim.bg,
                        borderColor: dim.border,
                    }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    data-testid="custom-cursor"
                >
                    {dim.label ? dim.label : state === "button" ? <ArrowRight className="h-3.5 w-3.5" /> : null}
                </motion.div>
            </div>
        </>
    );
}
